import { StatusCodes } from 'http-status-codes';
import getAPIService from '@api/service';
import { generateKey, encrypt } from '@utils/cipher';
import { packKey, serializeFingerprint, FingerprintInputs } from '@utils/key-mask';
import { Logger } from '@utils/logger';

interface LocatorRequest {
    type?: 'partners' | 'distributors';
}

interface LocatorState {
    companies: string;
    countries: string;
    keyCarrier: string;
}

async function fetchType(
    client: ReturnType<typeof getAPIService>,
    token: string | undefined,
    type: string,
    endpoint: 'partners' | 'countries',
): Promise<any> {
    try {
        const response = await client.request({
            timeout: 60 * 1000,
            validateStatus: (status: StatusCodes) => status === StatusCodes.OK,
            method: 'GET',
            url: `/svc/v1/partners-portal/locator/${type}/${endpoint}`,
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        return response.data;
    } catch (error) {
        Logger.error({ error });
        return null;
    }
}

export default {
    namespaced: true,

    state: (): LocatorState => ({
        companies: '',
        countries: '',
        keyCarrier: '',
    }),

    mutations: {
        setCompanies(state: LocatorState, value: string): void {
            state.companies = value;
        },
        setCountries(state: LocatorState, value: string): void {
            state.countries = value;
        },
        setKeyCarrier(state: LocatorState, value: string): void {
            state.keyCarrier = value;
        },
    },

    actions: {
        getCountryList(store: never, locator: LocatorRequest = {}): Promise<any> {
            const client = getAPIService(this.$context);
            const token = this.$context.env?.HEAD_SITE_MAIN_LOCATOR_BEARER_TOKEN;
            return fetchType(client, token, locator.type || 'partners', 'countries');
        },

        getCompanyList(store: never, locator: LocatorRequest = {}): Promise<any> {
            const client = getAPIService(this.$context);
            const token = this.$context.env?.HEAD_SITE_MAIN_LOCATOR_BEARER_TOKEN;
            return fetchType(client, token, locator.type || 'partners', 'partners');
        },

        async prefetchCompanies(
            store: { commit: (type: string, payload?: any) => void },
            { types, fingerprint }: {
                types: ('partners' | 'distributors')[];
                fingerprint: FingerprintInputs;
            },
        ): Promise<void> {
            const client = getAPIService(this.$context);
            const token = this.$context.env?.HEAD_SITE_MAIN_LOCATOR_BEARER_TOKEN;

            const [companiesByType, countriesData] = await Promise.all([
                Promise.all(types.map((t) => fetchType(client, token, t, 'partners'))),
                fetchType(client, token, types[0] || 'partners', 'countries'),
            ]);

            const companiesPayload = Object.fromEntries(
                types.map((t, i) => [t, companiesByType[i]?.data || []]),
            );

            const key = generateKey();
            const fingerStr = serializeFingerprint(fingerprint);

            const [carrier, encCompanies, encCountries] = await Promise.all([
                packKey(key, fingerStr),
                encrypt(JSON.stringify(companiesPayload), key),
                encrypt(JSON.stringify(countriesData?.data || []), key),
            ]);

            store.commit('setKeyCarrier', carrier);
            store.commit('setCompanies', encCompanies);
            store.commit('setCountries', encCountries);
        },
    },
};
